import React, { useState, useEffect, useCallback } from 'react';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';
import { effectiveAgentCommission, effectiveOwnerShare, effectiveAgentRemit, effectiveTotalPayable, remainingAgentRemit, remainingTotalPay, bookingAgentSummary } from '../../utils/externalCommissionMetrics.js';
import { DollarSign, Plus, Users, BarChart3 } from 'lucide-react';
import ListFilterPanel from '../common/ListFilterPanel.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { useAuth } from '../../context/AuthContext';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { formatIndianDate } from '../../utils/localDate.js';
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';


const ExternalCommissionList = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canDelete = user?.role === 'System Admin' || user?.role === 'Admin';
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vehicleOwnerFilter, setVehicleOwnerFilter] = useState('');
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const perPage = 20;

  useEffect(() => {
    externalCommissionService.getVehicleOwners()
      .then((res) => setVehicleOwners(res.data?.data || []))
      .catch(() => setVehicleOwners([]));
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.ml_sheet_id = search;
      if (statusFilter) filters.settlement_status = statusFilter;
      if (vehicleOwnerFilter) filters.vehicle_owner_id = vehicleOwnerFilter;
      const res = await externalCommissionService.getAll(currentPage, perPage, filters);
      setRecords((prev) => mergeLoadedPage(currentPage, res.data.data || [], prev));
      setTotalPages(res.data.pagination?.pages || 1);
      setTotalCount(res.data.pagination?.total || 0);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load vehicle owner commissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, vehicleOwnerFilter, currentPage]);

  usePaginatedListFetch({
    fetchPage: fetchRecords,
    setCurrentPage,
    resetPageDeps: [search, statusFilter, vehicleOwnerFilter],
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.menu-container')) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const toggleMenu = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleDelete = async (id) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to delete this vehicle owner commission? This action cannot be undone if payments exist.',
      'Delete Commission'
    );
    if (confirmed) {
      try {
        await externalCommissionService.delete(id);
        Alert.success('Commission deleted successfully!');
        if (records.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchRecords();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete commission');
      }
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setVehicleOwnerFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || statusFilter || vehicleOwnerFilter;

  const getStatusBadge = (status) => {
    const map = {
      PENDING: 'bg-yellow-100 text-yellow-700',
      PARTIAL: 'bg-blue-100 text-blue-700',
      PAID: 'bg-green-100 text-green-700',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN')}`;

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto px-0 sm:px-0">
        <ListPageHeader
          title="Commission Record"
          icon={DollarSign}
          titleClassName="text-secondary"
          iconClassName="w-5 h-5 text-secondary shrink-0"
          className="mb-4"
        >
          <Button mobileIconOnly variant="secondary" icon={BarChart3} onClick={() => navigate('/external-commission-dashboard')}>
            Dashboard
          </Button>
          <Button mobileIconOnly variant="secondary" icon={Users} onClick={() => navigate('/external-commissions/bulk-settlement')}>
            Agent Bulk Settlement
          </Button>
          <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/external-commissions/add')}>
            Add Commission
          </Button>
        </ListPageHeader>

        <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters}>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">ML Sheet ID</label>
            <SearchSubmitInput
              value={search}
              onSearch={setSearch}
              placeholder="Search by ML Sheet ID..."
              inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Vehicle Owner</label>
            <select
              value={vehicleOwnerFilter}
              onChange={(e) => setVehicleOwnerFilter(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
            >
              <option value="">All Vehicle Owners</option>
              {vehicleOwners.map((o) => (
                <option key={o.vehicle_owner_id} value={o.vehicle_owner_id}>
                  {o.owner_name || o.vehicle_owner_id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Settlement / Payment Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-sm font-medium text-gray-700"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="PARTIAL">Partial</option>
              <option value="PAID">Paid</option>
            </select>
          </div>
        </ListFilterPanel>

        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading commissions..." />
          </div>
        ) : records.length === 0 ? (
          <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">No commissions found</div>
        ) : (
          <>
            <div className="hidden xl:block bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="w-full overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Comm. ID</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">ML Sheet</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle Owner</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">To Pay</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Comm.</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Owner Share</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Agent Remit</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Due</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Agent</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {records.map((r) => (
                      <tr key={r.external_commission_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <Button variant="link" onClick={() => navigate(`/external-commissions/view/${r.external_commission_id}`)} className={listIdLinkClass}>
                            {r.external_commission_id}
                          </Button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                          <div>{r.ml_sheet_id}</div>
                          <div className="text-gray-400 text-xs mt-0.5">{formatIndianDate(r.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{r.vehicle_owner_name || r.vehicle_owner_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">{fmt(r.balance_freight)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-purple-700 font-medium">
                          {fmt(effectiveAgentCommission(r))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-green-700 font-medium">
                          {fmt(effectiveOwnerShare(r))}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                          <div>{fmt(effectiveAgentRemit(r))}</div>
                          <div className="text-gray-400 text-xs">Rem: {fmt(remainingAgentRemit(r))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                          <div>{fmt(effectiveTotalPayable(r))}</div>
                          <div className="text-gray-400 text-xs">Rem: {fmt(remainingTotalPay(r))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{bookingAgentSummary(r)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusBadge(r.settlement_status || r.payment_status)}`}>
                            {r.settlement_status || r.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <RowActionsMenu
                            className="relative menu-container"
                            isOpen={openMenuId === r.external_commission_id}
                            onToggle={() => toggleMenu(r.external_commission_id)}
                            onClose={() => setOpenMenuId(null)}
                            onView={() => navigate(`/external-commissions/view/${r.external_commission_id}`)}
                            onEdit={() => navigate(`/external-commissions/edit/${r.external_commission_id}`)}
                            onDelete={canDelete ? () => handleDelete(r.external_commission_id) : undefined}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 px-5 py-3 hidden xl:flex justify-between items-center border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing {records.length} of {totalCount} commission{totalCount !== 1 ? 's' : ''}
                </div>
                <ListPagination
          currentPage={currentPage}
          totalPages={totalPages}
          loading={loading && currentPage > 1}
          onNext={() => { setLoading(true); setCurrentPage((prev) => Math.min(totalPages, prev + 1)); }}
        />
              </div>
            </div>

            <div className="xl:hidden space-y-3 p-1 sm:p-0">
              {records.map((r) => (
                <div key={r.external_commission_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                  <div className="flex justify-between items-start gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                      <h3
                        className={listIdLinkMobileClass}
                        onClick={() => navigate(`/external-commissions/view/${r.external_commission_id}`)}
                      >
                        {r.external_commission_id}
                      </h3>
                      <p className="text-xs text-gray-500 break-all">ML Sheet: {r.ml_sheet_id}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatIndianDate(r.created_at)}</p>
                      <p className="text-xs text-gray-500">{r.vehicle_owner_name}</p>
                    </div>
                    <div className="flex items-start gap-1.5 shrink-0">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${getStatusBadge(r.settlement_status || r.payment_status)}`}>
                        {r.settlement_status || r.payment_status}
                      </span>
                      <RowActionsMenu
                        className="relative menu-container"
                        isOpen={openMenuId === r.external_commission_id}
                        onToggle={() => toggleMenu(r.external_commission_id)}
                        onClose={() => setOpenMenuId(null)}
                        onView={() => navigate(`/external-commissions/view/${r.external_commission_id}`)}
                        onEdit={() => navigate(`/external-commissions/edit/${r.external_commission_id}`)}
                        onDelete={canDelete ? () => handleDelete(r.external_commission_id) : undefined}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div className="min-w-0"><span className="text-gray-500">To Pay:</span> <span className="font-semibold">{fmt(r.balance_freight)}</span></div>
                    <div className="min-w-0"><span className="text-gray-500">Booking Agent:</span> <span className="font-semibold">{bookingAgentSummary(r)}</span></div>
                    <div className="min-w-0"><span className="text-gray-500">Agent Comm:</span> <span className="font-semibold text-purple-700">{fmt(effectiveAgentCommission(r))}</span></div>
                    <div className="min-w-0"><span className="text-gray-500">Owner Share:</span> <span className="font-semibold text-green-700">{fmt(effectiveOwnerShare(r))}</span></div>
                    <div className="min-w-0"><span className="text-gray-500">Agent Remit:</span> <span className="font-semibold">{fmt(effectiveAgentRemit(r))}</span></div>
                    <div className="min-w-0"><span className="text-gray-500">Pay Due:</span> <span className="font-semibold">{fmt(effectiveTotalPayable(r))}</span></div>
                    <div><span className="text-gray-500">Remit/Paid:</span> <span className="font-semibold">{fmt(r.amount_received_from_agent ?? r.amount_received_from_owner)} / {fmt((r.amount_paid_to_owner || 0) + (r.amount_paid_to_agent || 0))}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ExternalCommissionList;
